import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, MoreVertical, Plus, Link as LinkIcon, AlertCircle, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { auditDataService } from '@/services/audit-data-service';
import type { AuditException } from '@/types/asset-audit';
import { useAuditPermissions } from '@/hooks/use-permissions';

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'Open':
      return <AlertCircle className="h-4 w-4" />;
    case 'In Progress':
      return <Clock className="h-4 w-4" />;
    case 'Resolved':
      return <CheckCircle2 className="h-4 w-4" />;
    case 'Closed':
      return <XCircle className="h-4 w-4" />;
    default:
      return <AlertCircle className="h-4 w-4" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Open':
      return 'destructive';
    case 'In Progress':
      return 'default';
    case 'Resolved':
      return 'success';
    case 'Closed':
      return 'secondary';
    default:
      return 'default';
  }
};

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'Critical':
      return 'bg-red-500 text-white';
    case 'High':
      return 'bg-orange-500 text-white';
    case 'Medium':
      return 'bg-yellow-500 text-white';
    case 'Low':
      return 'bg-blue-500 text-white';
    default:
      return 'bg-gray-500 text-white';
  }
};

// Helper function to get activity background color - extracted to fix nested ternary (S3358)
const getActivityBgColor = (type: string) => {
  switch (type) {
    case 'approval':
      return 'bg-green-100';
    case 'workitem':
      return 'bg-blue-100';
    case 'comment':
      return 'bg-purple-100';
    case 'status':
      return 'bg-orange-100';
    case 'assignment':
      return 'bg-yellow-100';
    default:
      return 'bg-gray-100';
  }
};

export default function ExceptionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Permission checks for exception actions (using audit permissions since exceptions are part of audits)
  const { canEditAudits, canCreateAudits } = useAuditPermissions();
  
  const [showCreateWorkItem, setShowCreateWorkItem] = useState(false);
  const [showLinkWorkItem, setShowLinkWorkItem] = useState(false);
  const [exception, setException] = useState<AuditException | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch the exception by ID
  useEffect(() => {
    const loadException = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const result = await auditDataService.getExceptionById(id);
        setException(result);
      } catch (error) {
        console.error('Failed to load exception:', error);
        setException(null);
      } finally {
        setLoading(false);
      }
    };

    loadException();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center">
          <p className="text-muted-foreground">Loading exception...</p>
        </div>
      </div>
    );
  }

  if (!exception) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Exception Not Found</h2>
          <p className="text-muted-foreground mb-4">The exception you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/audits/exceptions')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Exceptions
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/audits/exceptions')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">{exception.exceptionNumber || exception.id}</h1>
          </div>
          <p className="text-muted-foreground">{exception.description || exception.title}</p>
        </div>
        <div className="flex items-center gap-2">
          {canEditAudits && (
            <Button variant="outline" size="sm">
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>Export Details</DropdownMenuItem>
              <DropdownMenuItem>Share</DropdownMenuItem>
              {canCreateAudits && (
                <DropdownMenuItem>Duplicate</DropdownMenuItem>
              )}
              {canEditAudits && (
                <DropdownMenuItem className="text-destructive">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Status Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Status</p>
              <Badge variant={getStatusColor(exception.status) as any} className="gap-1">
                {getStatusIcon(exception.status)}
                {exception.status}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Severity</p>
              <Badge className={getSeverityColor(exception.severity)}>
                {exception.severity}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Reported</p>
              <p className="text-sm font-medium">{new Date(exception.reportedDate).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Reported By</p>
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-xs">
                    {exception.reportedByName ? exception.reportedByName.split(' ').map(n => n[0]).join('') : 'NA'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium">{exception.reportedByName ?? 'Unknown'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="asset">Asset Information</TabsTrigger>
          <TabsTrigger value="workitems">Work Items</TabsTrigger>
          <TabsTrigger value="resolution">Resolution</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Exception Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <p className="font-medium">{exception.exceptionType}</p>
                </div>
                <Separator />
                <div>
                  <Label className="text-muted-foreground">Description</Label>
                  <p className="text-sm">{exception.description || exception.title}</p>
                </div>
                <Separator />
                <div>
                  <Label className="text-muted-foreground">Reported By</Label>
                  <p className="font-medium">{exception.reportedByName}</p>
                </div>
                <Separator />
                <div>
                  <Label className="text-muted-foreground">Audit Reference</Label>
                  <Button
                    variant="link"
                    className="h-auto p-0 font-medium"
                    onClick={() => navigate(`/audits/${exception.auditId}`)}
                  >
                    {exception.auditNumber || exception.auditId}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Impact Assessment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Financial Impact</Label>
                  <p className="text-2xl font-bold text-destructive">
                    $0
                  </p>
                </div>
                <Separator />
                <div>
                  <Label className="text-muted-foreground">Operational Impact</Label>
                  <p className="text-sm">Moderate - Asset unavailable for use</p>
                </div>
                <Separator />
                <div>
                  <Label className="text-muted-foreground">Compliance Risk</Label>
                  <Badge variant="outline" className="text-orange-600 border-orange-600">
                    Medium Risk
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Root Cause Analysis</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Primary Cause</Label>
                <p className="font-medium">Process Failure</p>
              </div>
              <Separator />
              <div>
                <Label className="text-muted-foreground">Contributing Factors</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="secondary">Insufficient Training</Badge>
                  <Badge variant="secondary">Policy Non-Compliance</Badge>
                </div>
              </div>
              <Separator />
              <div>
                <Label className="text-muted-foreground">Narrative</Label>
                <p className="text-sm">
                  Asset was not properly logged during transfer between departments. 
                  The transfer process was not followed according to policy, resulting in 
                  location discrepancy during audit.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Asset Information Tab */}
        <TabsContent value="asset" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Asset Identity</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground">Asset Code</Label>
                  <p className="font-medium">{exception.assetCode}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Asset Name</Label>
                  <p className="font-medium">{exception.assetName}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Category</Label>
                  <p className="font-medium">IT Equipment</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Serial Number</Label>
                  <p className="font-medium">SN-2024-12345</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Manufacturer</Label>
                  <p className="font-medium">Dell Inc.</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Model</Label>
                  <p className="font-medium">Latitude 5420</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Book Value</Label>
                  <p className="font-medium">N/A</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Purchase Date</Label>
                  <p className="font-medium">Jan 15, 2023</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Location Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground">Location</Label>
                  <p className="font-medium">{exception.location ?? 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Expected Location</Label>
                  <p className="font-medium">{exception.expectedLocation ?? 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Actual Location</Label>
                  <p className="font-medium">{exception.actualLocation ?? 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Reported By</Label>
                  <p className="font-medium">{exception.reportedByName ?? 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Work Items Tab */}
        <TabsContent value="workitems" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Linked Work Items</CardTitle>
                  <CardDescription>Tasks and actions to resolve this exception</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => setShowCreateWorkItem(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Work Item
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowLinkWorkItem(true)}>
                    <LinkIcon className="mr-2 h-4 w-4" />
                    Link Existing
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Work Item Summary */}
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">2 Total</span>
                  <span className="text-green-600">1 Completed</span>
                  <span className="text-blue-600">1 In Progress</span>
                  <span className="text-muted-foreground">0 Overdue</span>
                </div>

                <Separator />

                {/* Work Items List */}
                <div className="space-y-3">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">WI-2025-001</p>
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              Completed
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">Investigate asset location discrepancy</p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
                            <span>Type: Investigation</span>
                            <span>•</span>
                            <span>Assigned: John Smith</span>
                            <span>•</span>
                            <span>Due: Jan 20, 2025</span>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">View</Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">WI-2025-002</p>
                            <Badge variant="outline" className="text-blue-600 border-blue-600">
                              In Progress
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">Update asset location in system</p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
                            <span>Type: System Update</span>
                            <span>•</span>
                            <span>Assigned: Sarah Johnson</span>
                            <span>•</span>
                            <span>Due: Jan 25, 2025</span>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">View</Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resolution Tab */}
        <TabsContent value="resolution" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Resolution Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Resolution Type</Label>
                <p className="font-medium">Record Corrected</p>
              </div>
              <Separator />
              <div>
                <Label className="text-muted-foreground">Resolution Description</Label>
                <p className="text-sm">
                  Asset location has been verified and updated in the system. 
                  Transfer documentation has been completed retroactively.
                </p>
              </div>
              <Separator />
              <div>
                <Label className="text-muted-foreground">Preventive Measures</Label>
                <p className="text-sm">
                  • Implement mandatory transfer form completion before physical asset movement<br />
                  • Schedule quarterly training on asset transfer procedures<br />
                  • Add system validation to prevent location updates without proper documentation
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Verification</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground">Verified By</Label>
                  <p className="font-medium">Michael Chen</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Verification Date</Label>
                  <p className="font-medium">Jan 22, 2025</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Management Approval</Label>
                  <Badge variant="outline" className="text-green-600 border-green-600">
                    Approved
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Follow-up Date</Label>
                  <p className="font-medium">Apr 22, 2025</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Activity Timeline</CardTitle>
              <CardDescription>Complete history of all actions and changes</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px] pr-4">
                <div className="space-y-4">
                  {[
                    {
                      date: 'Jan 22, 2025 2:30 PM',
                      user: 'Michael Chen',
                      action: 'Verified resolution and approved closure',
                      type: 'approval'
                    },
                    {
                      date: 'Jan 21, 2025 4:15 PM',
                      user: 'Sarah Johnson',
                      action: 'Completed work item WI-2025-002 - System updated',
                      type: 'workitem'
                    },
                    {
                      date: 'Jan 20, 2025 11:00 AM',
                      user: 'John Smith',
                      action: 'Completed work item WI-2025-001 - Investigation complete',
                      type: 'workitem'
                    },
                    {
                      date: 'Jan 18, 2025 9:45 AM',
                      user: 'John Smith',
                      action: 'Added comment: Asset found in Room 305, updating records',
                      type: 'comment'
                    },
                    {
                      date: 'Jan 17, 2025 3:20 PM',
                      user: 'System',
                      action: 'Work item WI-2025-002 created and linked',
                      type: 'system'
                    },
                    {
                      date: 'Jan 17, 2025 3:15 PM',
                      user: 'System',
                      action: 'Work item WI-2025-001 created and linked',
                      type: 'system'
                    },
                    {
                      date: 'Jan 17, 2025 2:00 PM',
                      user: 'Emily Davis',
                      action: 'Changed status from Open to In Progress',
                      type: 'status'
                    },
                    {
                      date: 'Jan 17, 2025 1:45 PM',
                      user: 'Emily Davis',
                      action: 'Assigned to John Smith',
                      type: 'assignment'
                    },
                    {
                      date: 'Jan 15, 2025 10:30 AM',
                      user: 'Emily Davis',
                      action: 'Exception created during audit AUD-2025-001',
                      type: 'created'
                    }
                  ].map((activity, index) => (
                    <div key={`activity-${activity.date}-${activity.type}`} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`rounded-full p-2 ${getActivityBgColor(activity.type)}`}>
                          <CheckCircle2 className="h-4 w-4" />
                        </div>
                        {index < 8 && <div className="w-px h-full bg-border mt-2" />}
                      </div>
                      <div className="flex-1 pb-4">
                        <p className="text-sm font-medium">{activity.action}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground">{activity.user}</span>
                          <span className="text-xs text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground">{activity.date}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Work Item Dialog */}
      <Dialog open={showCreateWorkItem} onOpenChange={setShowCreateWorkItem}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Work Item</DialogTitle>
            <DialogDescription>
              Create a new work item to resolve this exception
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input id="title" placeholder="Enter work item title" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="type">Type *</Label>
                <Select>
                  <SelectTrigger id="type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="task">Task</SelectItem>
                    <SelectItem value="investigation">Investigation</SelectItem>
                    <SelectItem value="maintenance">Maintenance Request</SelectItem>
                    <SelectItem value="documentation">Documentation Update</SelectItem>
                    <SelectItem value="training">Training Requirement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select>
                  <SelectTrigger id="priority">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" placeholder="Describe the work to be done" rows={4} />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="assignee">Assign To *</Label>
                <Select>
                  <SelectTrigger id="assignee">
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="john">John Smith</SelectItem>
                    <SelectItem value="sarah">Sarah Johnson</SelectItem>
                    <SelectItem value="michael">Michael Chen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="duedate">Due Date</Label>
                <Input id="duedate" type="date" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateWorkItem(false)}>
              Cancel
            </Button>
            <Button onClick={() => setShowCreateWorkItem(false)}>
              Create & Link Work Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Work Item Dialog */}
      <Dialog open={showLinkWorkItem} onOpenChange={setShowLinkWorkItem}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Link Existing Work Item</DialogTitle>
            <DialogDescription>
              Search and link existing work items to this exception
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Search work items..." />
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Card key={`workitem-${i}`} className="cursor-pointer hover:bg-accent">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <p className="font-medium">WI-2025-00{i}</p>
                          <p className="text-sm text-muted-foreground">Sample work item title</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>Type: Task</span>
                            <span>•</span>
                            <span>Status: Open</span>
                          </div>
                        </div>
                        <input type="checkbox" className="mt-1 bg-white dark:bg-black disabled:bg-transparent" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkWorkItem(false)}>
              Cancel
            </Button>
            <Button onClick={() => setShowLinkWorkItem(false)}>
              Link Selected Items
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
